import React, { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Switch } from "@progress/kendo-react-inputs";
import { Button } from "@progress/kendo-react-buttons";
import {
  MessageSquareHeart,
  Star,
  CheckCircle,
  AlertTriangle,
  Eye,
  EyeOff,
  Award,
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import appConfig from "../../config/appConfig";
import { feedbackApi } from "../../api/feedbackApi";
import SocialProof from "../../components/SocialProof";
import { usePermission } from "../../hooks/usePermission";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { LoadingSpinner, ErrorCard } from "../../components/LoadingSpinner";
/**
 * Super Admin → Feedback & Testimonials.
 * Collect + moderate provider feedback, and decide whether/where (if at all)
 * approved testimonials are shown. Nothing is public until `publish` != 'off'
 * AND an item is approved/featured — so nothing is ever published "raw".
 */
const isSuperAdmin = (user) =>
  String(user?.userRole || user?.role || "").toUpperCase() === "SUPER ADMIN" ||
  String(user?.userRole || user?.role || "").toUpperCase() === "ADMIN";

const card = {
  background: "#fff",
  border: "1px solid #E8ECF0",
  borderRadius: 14,
  padding: "22px 26px",
  marginBottom: 20,
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
};
const chipStyle = (bg, fg) => ({
  background: bg,
  color: fg,
  padding: "2px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 700,
  display: "inline-block",
});
const statusChip = (s) =>
  ({
    featured: chipStyle("#FEF9E7", "#B7791F"),
    approved: chipStyle("#ECFDF5", "#059669"),
    hidden: chipStyle("#FEF2F2", "#DC2626"),
    new: chipStyle("#F1F5F9", "#475569"),
  })[s] || chipStyle("#F1F5F9", "#475569");

const Stars = ({ n }) => (
  <span style={{ display: "inline-flex", gap: 1 }}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        size={14}
        color="#F59E0B"
        fill={i <= n ? "#F59E0B" : "none"}
        strokeWidth={2}
      />
    ))}
  </span>
);

export default function FeedbackAdminPage() {
  const { user } = useAuth();
  const { can, canAny, canAll } = usePermission();
  const [cfg, setCfg] = useState(() => appConfig.getFeedbackConfig());
  //const [rows, setRows] = useState([]);
  const [savedMsg, setSavedMsg] = useState(null);
  const [collectValue, setCollectValue] = useState("");
  const [publishValue, setPublishValue] = useState("");
  const [accessPermissions, setAccessPermissions] = useState({});

  const [filterStatus, setFilterStatus] = useState("All");
  const [gridLoading, setGridLoading] = useState(false);

  const [socialProofRefreshKey, setSocialProofRefreshKey] = useState(0);

  const FEEDBACK_STATUS_OPTIONS = ["All", "New","Hidden", "Approved", "Featured"];

  //const load = () => feedbackApi.list().then((res) => setRows(res?.data?.Data || [])).catch(() => {});
  //useEffect(() => { load(); }, []);
  useEffect(
    () => appConfig.subscribe(() => setCfg(appConfig.getFeedbackConfig())),
    [],
  );

  if (!isSuperAdmin(user)) return <Navigate to="/" replace />;

  const saveCollect = async (v) => {
    setCfg((c) => ({ ...c, collect: v }));
    try {
      await appConfig.saveFeedbackConfig({ collect: v, publish: publishValue });
      setSavedMsg("Saved.");
    } catch (e) {
      setSavedMsg(`Couldn’t save: ${e.message}`);
    }
  };
  //const saveCollect = async (v) => { setCfg((c) => ({ ...c, collect: v })); try { await appConfig.saveFeedbackConfig({ collect: v }); setSavedMsg('Saved.'); } catch (e) { setSavedMsg(`Couldn’t save: ${e.message}`); } };
  //const savePublish = async (v) => { setCfg((c) => ({ ...c, publish: v })); try { await appConfig.saveFeedbackConfig({ publish: v }); setSavedMsg('Saved.'); } catch (e) { setSavedMsg(`Couldn’t save: ${e.message}`); } };
  const savePublish = async (v) => {
    setCfg((c) => ({ ...c, publish: v }));
    try {
      await appConfig.saveFeedbackConfig({ publish: v, collect: collectValue });
      setSavedMsg("Saved.");
    } catch (e) {
      setSavedMsg(`Couldn’t save: ${e.message}`);
    }
  };

  //const accessPermissions = {
  //    canChangeConfig: can('superAdmin', 'feedbacks', 'changeConfig'),
  //    canApproveFeedback: can('superAdmin', 'feedbacks', 'approveFeedback'),
  //    canHideAndUnhideFeedback: can('superAdmin', 'feedbacks', 'hideAndUnhideFeedback'),
  //    canFeaturedFeedback: can('superAdmin', 'feedbacks', 'feacturedFeedback'),
  //};

  const styles = {
    overlay: {
      position: "fixed",
      inset: 0, // replaces top/left/width/height
      backgroundColor: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 9999,
    },
    gridOverlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: "rgba(255,255,255,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1000,
    },
  };

  useEffect(() => {
    setAccessPermissions({
      canViewFeedbacks: can("superAdmin", "feedbacks", "viewFeedbacks"),
      canChangeConfig: can("superAdmin", "feedbacks", "changeConfig"),
      canApproveFeedback: can("superAdmin", "feedbacks", "approveFeedback"),
      canHideAndUnhideFeedback: can(
        "superAdmin",
        "feedbacks",
        "hideAndUnhideFeedback",
      ),
      canFeaturedFeedback: can("superAdmin", "feedbacks", "feacturedFeedback"),
    });
  }, [user]);

  const [rows, setRows] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);

  const [page, setPage] = useState({
    skip: 0,
    take: 10,
  });

  const [sort, setSort] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const searchTimeoutRef = useRef(null);

  const formatDate = (apiDate) => {
    if (!apiDate) return "";

    return new Date(apiDate).toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const getFeedbacks = async ({
    requestedSkip = page.skip,
    requestedTake = page.take,
    requestedSearchText = searchText,
    requestedStatus = filterStatus,
    requestedSort = sort,
  } = {}) => {
    setGridLoading(true);
    setError("");

    try {
      // Replace this with your existing feedback API method.
      const response = await feedbackApi.list2({
        searchText: requestedSearchText,
        status: requestedStatus === "All" ? "" : requestedStatus,
        startIndex: requestedSkip,
        pageSize: requestedTake,
        sortColumnName: requestedSort[0]?.field ?? "",
        sortColumnDirection: requestedSort[0]?.dir ?? "",
      });

      const data = response?.data ?? [];

      // console.log(JSON.stringify(response));

      // alert(response?.totalRecord);

      //setRows(data);

      setRows(
        data.map((row) => ({
          ...row,
          createdAt: formatDate(row.createdAt),
        })),
      );
      setTotalRecord(Number(response?.totalRecord ?? 0));

      setGridLoading(false);
    } catch (err) {
      setError(err?.message || "Failed to load feedback.");
      setRows([]);
      setTotalRecord(0);
      setGridLoading(false);
    } finally {
      setGridLoading(false);
    }
  };

  const moderate = async (id, status) => {
    await feedbackApi.setStatus(id, status);
    await getFeedbacks({
      requestedSkip: page.skip,
      requestedTake: page.take,
      requestedSearchText: searchText,
      requestedStatus: filterStatus,
      requestedSort: sort,
    });

    setSocialProofRefreshKey((prev) => prev + 1);
  };

  useEffect(() => {
    getFeedbacks();

    return () => clearTimeout(searchTimeoutRef.current);
    // Initial load only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (event) => {
    const nextPage = event.page;

    setPage({
      skip: nextPage.skip,
      take: nextPage.take,
    });

    getFeedbacks({
      requestedSkip: nextPage.skip,
      requestedTake: nextPage.take,
      requestedSearchText: searchText,
      requestedStatus: filterStatus,
      requestedSort: sort,
    });
  };

  const handleSortChange = (event) => {
    const nextSort = event.sort || [];

    setSort(nextSort);
    setPage((prev) => ({ ...prev, skip: 0 }));

    getFeedbacks({
      requestedSkip: 0,
      requestedTake: page.take,
      requestedSearchText: searchText,
      requestedStatus: filterStatus,
      requestedSort: nextSort,
    });
  };

  const handleSearchChange = (value) => {
    setSearchText(value);
    setPage((prev) => ({ ...prev, skip: 0 }));

    clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      getFeedbacks({
        requestedSkip: 0,
        requestedTake: page.take,
        requestedSearchText: value,
        requestedStatus: filterStatus,
        requestedSort: sort,
      });
    }, 400);
  };

  const FeedbackDateCell = (props) => (
    <td>
      {props.dataItem.createdAt ??
        // ? new Date(props.dataItem.createdAt).toLocaleDateString()
        "—"}
    </td>
  );

  const FeedbackRatingCell = (props) => (
    <td>
      <Stars n={Number(props.dataItem.rating) || 0} />
    </td>
  );

  const FeedbackTestimonialCell = (props) => {
    const feedback = props.dataItem;

    return (
      <td>
        {feedback.testimonial
          ? `"${feedback.testimonial}"${
              feedback.consent ? "" : " (no consent)"
            }`
          : "—"}
      </td>
    );
  };

  const FeedbackFromCell = (props) => {
    const feedback = props.dataItem;

    return (
      <td>
        {feedback.name || "—"}
        {feedback.org && (
          <div style={{ fontSize: 12, color: "#6B7280" }}>{feedback.org}</div>
        )}
      </td>
    );
  };

  const FeedbackStatusCell = (props) => (
    <td>
      <span style={statusChip(props.dataItem.status)}>
        {props.dataItem.status}
      </span>
    </td>
  );

  const FeedbackActionsCell = ({
    dataItem: f,
    accessPermissions,
    moderate,
  }) => {
    const canFeature = f.testimonial && f.consent;

    return (
      <td>
        <div style={{ display: "flex", gap: 6 }}>
          {accessPermissions.canApproveFeedback && f.status !== "approved" && (
            <Button size="small" onClick={() => moderate(f.id, "approved")}>
              <CheckCircle />
            </Button>
          )}

          {accessPermissions.canFeaturedFeedback &&
            canFeature &&
            f.status !== "featured" && (
              <Button size="small" onClick={() => moderate(f.id, "featured")}>
                <Award />
              </Button>
            )}

          {accessPermissions.canHideAndUnhideFeedback &&
            (f.status !== "hidden" ? (
              <Button size="small" onClick={() => moderate(f.id, "hidden")}>
                <EyeOff />
              </Button>
            ) : (
              <Button size="small" onClick={() => moderate(f.id, "new")}>
                <Eye />
              </Button>
            ))}
        </div>
      </td>
    );
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1>Feedback &amp; Testimonials</h1>
          <p style={{ margin: "4px 0 0", color: "#6B7280", fontSize: 14 }}>
            Collect provider feedback and choose whether — and where — approved
            testimonials appear. Nothing is public until you opt in.
          </p>
        </div>
      </div>

      {/* Configuration */}
      {accessPermissions.canChangeConfig && (
        <>
          <div style={card}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 14,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: 10,
                  background: "rgba(13,148,136,0.1)",
                  color: "#0F766E",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <MessageSquareHeart size={20} />
              </div>
              <div style={{ flex: 1, minWidth: 240 }}>
                <h2
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    margin: 0,
                    color: "#0F172A",
                  }}
                >
                  Configuration
                </h2>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginTop: 14,
                  }}
                >
                  <Switch
                    checked={cfg.collect}
                    data-cy="feedbackConfigSwitch"
                    name="feedbackConfigSwitch"
                    onChange={(e) => {
                      saveCollect(e.value);
                      setCollectValue(e.value);
                    }}
                  />
                  <span
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: cfg.collect ? "#166534" : "#64748B",
                    }}
                  >
                    Collect feedback ({cfg.collect ? "ON" : "OFF"}) — shows the
                    “Rate your experience” link
                  </span>
                </div>
                <div style={{ marginTop: 16 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: 10,
                      fontWeight: 700,
                      color: "#94A3B8",
                      textTransform: "uppercase",
                      letterSpacing: 0.8,
                      marginBottom: 6,
                    }}
                  >
                    Publish approved testimonials
                  </label>
                  <select
                    value={cfg.publish}
                    data-cy="showFeedbackOptionSelect"
                    onChange={(e) => {
                      savePublish(e.target.value);
                      setPublishValue(e.target.value);
                    }}
                    style={{
                      background: "#F8FAFB",
                      border: "1px solid #E8ECF0",
                      borderRadius: 10,
                      padding: "9px 12px",
                      font: "inherit",
                      fontSize: 14,
                      color: "#1A1D21",
                      outline: "none",
                      minWidth: 240,
                    }}
                  >
                    <option value="off">
                      Off — not shown anywhere (default)
                    </option>
                    <option value="dashboard">On the Home dashboard</option>
                    <option value="help">On the Help &amp; Support page</option>
                  </select>
                  <div
                    style={{ fontSize: 12.5, color: "#94A3B8", marginTop: 6 }}
                  >
                    Not shown on the login page. Only <strong>approved</strong>/
                    <strong>featured</strong> testimonials with consent appear.
                  </div>
                </div>
                {savedMsg && (
                  <div
                    style={{
                      marginTop: 12,
                      fontSize: 12.5,
                      color: savedMsg.startsWith("Saved")
                        ? "#166534"
                        : "#DC2626",
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    {savedMsg.startsWith("Saved") ? (
                      <CheckCircle size={14} />
                    ) : (
                      <AlertTriangle size={14} />
                    )}
                    {savedMsg}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Live preview of the social-proof strip */}
      <div style={card}>
        <h2
          style={{
            fontSize: 16,
            fontWeight: 700,
            margin: "0 0 4px",
            color: "#0F172A",
          }}
        >
          Preview
        </h2>
        <p style={{ fontSize: 13, color: "#6B7280", margin: "0 0 14px" }}>
          What the social-proof strip shows with your currently
          approved/featured testimonials
          {cfg.publish === "off"
            ? " (publishing is Off, so it is not live yet)"
            : ` (live on the ${cfg.publish === "dashboard" ? "dashboard" : "Help page"})`}
          .
        </p>
        {/*<SocialProof forcePreview />*/}

        <SocialProof forcePreview refreshKey={socialProofRefreshKey} />
      </div>

      {/* Moderation */}
      {accessPermissions.canViewFeedbacks && (
        <>
          <div style={{ position: "relative" }}>
            {gridLoading && (
              <div style={styles.gridOverlay}>
                <LoadingSpinner
                  data-cy="grid-loader"
                  message="Loading feedbacks..."
                />
              </div>
            )}
            <div style={{ padding: "0 26px 18px" }}>
              {/*<div*/}
              {/*    style={{*/}
              {/*        display: "flex",*/}
              {/*        gap: 12,*/}
              {/*        alignItems: "center",*/}
              {/*        flexWrap: "wrap",*/}
              {/*        margin: "16px 0",*/}
              {/*    }}*/}
              {/*>*/}
              {/*    <input*/}
              {/*        type="text"*/}
              {/*        placeholder="Search feedback..."*/}
              {/*        value={searchText}*/}
              {/*        onChange={(e) => handleSearchChange(e.target.value)}*/}
              {/*        style={{*/}
              {/*            width: 280,*/}
              {/*            padding: "10px 12px",*/}
              {/*            border: "1px solid #D1D5DB",*/}
              {/*            borderRadius: 8,*/}
              {/*            fontSize: 14,*/}
              {/*        }}*/}
              {/*    />*/}

              {/*    <DropDownList*/}
              {/*        data={FEEDBACK_STATUS_OPTIONS}*/}
              {/*        value={filterStatus}*/}
              {/*        onChange={(event) => {*/}
              {/*            const selectedStatus = event.value;*/}

              {/*            setFilterStatus(selectedStatus);*/}
              {/*            setPage((prev) => ({ ...prev, skip: 0 }));*/}

              {/*            getFeedbacks({*/}
              {/*                requestedSkip: 0,*/}
              {/*                requestedTake: page.take,*/}
              {/*                requestedSearchText: searchText,*/}
              {/*                requestedStatus: selectedStatus,*/}
              {/*                requestedSort: sort,*/}
              {/*            });*/}
              {/*        }}*/}
              {/*        style={{ width: 190 }}*/}
              {/*    />*/}

              {/*    {(searchText || filterStatus !== "All") && (*/}
              {/*        <button*/}
              {/*            type="button"*/}
              {/*            onClick={() => {*/}
              {/*                clearTimeout(searchTimeoutRef.current);*/}

              {/*                setSearchText("");*/}
              {/*                setFilterStatus("All");*/}
              {/*                setPage((prev) => ({ ...prev, skip: 0 }));*/}
              {/*                setSort([]);*/}

              {/*                getFeedbacks({*/}
              {/*                    requestedSkip: 0,*/}
              {/*                    requestedTake: page.take,*/}
              {/*                    requestedSearchText: "",*/}
              {/*                    requestedStatus: "All",*/}
              {/*                    requestedSort: [],*/}
              {/*                });*/}
              {/*            }}*/}
              {/*            style={{*/}
              {/*                border: "none",*/}
              {/*                background: "none",*/}
              {/*                color: "#0D9488",*/}
              {/*                fontWeight: 600,*/}
              {/*                cursor: "pointer",*/}
              {/*            }}*/}
              {/*        >*/}
              {/*            Clear filters*/}
              {/*        </button>*/}
              {/*    )}*/}
              {/*</div>*/}

              <div
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  margin: "16px 0",
                }}
              >
                <input
                                  type="text"
                  name="txtSearch"
                  placeholder="Search feedback..."
                  value={searchText}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  style={{
                    width: 240,
                    height: 42,
                    boxSizing: "border-box",
                    padding: "0 12px",
                    border: "1px solid #D1D5DB",
                    borderRadius: 8,
                    fontSize: 14,
                  }}
                />

                <DropDownList
                                  data={FEEDBACK_STATUS_OPTIONS}
                                  name="selStatus"
                  value={filterStatus}
                  onChange={(event) => {
                    const selectedStatus = event.value;

                    setFilterStatus(selectedStatus);
                    setPage((prev) => ({ ...prev, skip: 0 }));

                    getFeedbacks({
                      requestedSkip: 0,
                      requestedTake: page.take,
                      requestedSearchText: searchText,
                      requestedStatus: selectedStatus,
                      requestedSort: sort,
                    });
                  }}
                  style={{
                    width: 240,
                    height: 42,
                    boxSizing: "border-box",
                  }}
                />

                {(searchText || filterStatus !== "All") && (
                  <button
                    type="button"
                    onClick={() => {
                      clearTimeout(searchTimeoutRef.current);

                      setSearchText("");
                      setFilterStatus("All");
                      setPage((prev) => ({ ...prev, skip: 0 }));
                      setSort([]);

                      getFeedbacks({
                        requestedSkip: 0,
                        requestedTake: page.take,
                        requestedSearchText: "",
                        requestedStatus: "All",
                        requestedSort: [],
                      });
                    }}
                    style={{
                      border: "none",
                      background: "none",
                      color: "#0D9488",
                      fontWeight: 600,
                      cursor: "pointer",
                        whiteSpace: "nowrap",
                        fontSize: 14,
                    }}
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {error && (
                <div style={{ color: "#DC2626", padding: 12 }}>
                  {error}
                  <button
                    type="button"
                    onClick={() => getFeedbacks()}
                    style={{ marginLeft: 12 }}
                  >
                    Retry
                  </button>
                </div>
              )}

              {loading && (
                <div style={{ padding: 12 }}>Loading feedback...</div>
              )}

                          <Grid 
                          data-cy="feedbackGrid"
                              name="feedbackGrid"           
                data={rows}
                dataItemKey="id"
                skip={page.skip}
                take={page.take}
                total={totalRecord}
                pageable={{
                  buttonCount: 5,
                  pageSizes: [5, 10, 20, 50],
                  info: true,
                }}
                sortable
                sort={sort}
                onPageChange={handlePageChange}
                onSortChange={handleSortChange}
                resizable
                style={{ minHeight: 250 }}
              >
                <GridColumn
                  field="createdAt"
                  title="When"
                  width="150px"
                  cells={{ data: FeedbackDateCell }}
                />

                <GridColumn
                  field="rating"
                  title="Rating"
                  width="130px"
                  cells={{ data: FeedbackRatingCell }}
                />

                <GridColumn field="comment" title="Comment" width="250px" />

                <GridColumn
                  field="testimonial"
                  title="Testimonial"
                  width="280px"
                  cells={{ data: FeedbackTestimonialCell }}
                />

                <GridColumn
                  field="name"
                  title="From"
                  width="180px"
                  cells={{ data: FeedbackFromCell }}
                />

                <GridColumn
                  field="status"
                  title="Status"
                  width="140px"
                  cells={{ data: FeedbackStatusCell }}
                />

                <GridColumn
                  title="Actions"
                  width="180px"
                  sortable={false}
                  cells={{
                    data: (props) => (
                      <FeedbackActionsCell
                        {...props}
                        accessPermissions={accessPermissions}
                        moderate={moderate}
                      />
                    ),
                  }}
                />
              </Grid>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
